/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: PlexLibrary
 * File     : PlexLibrary.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the PlexLibrary view and coordinating data fetching.
 *   Exports: PlexLibrary
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/Lock
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/plexlibrary" element={<PlexLibrary />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Chip, Stack, Paper, Button, Pagination, Drawer, Divider } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import { plexMovies, plexStatus, buildApiUrl, getWinnersSummary, getWatchmodeSources, getMovieByImdb, getMovieByTitle } from '../services/api';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PlexLibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [movies, setMovies] = useState([]);
  const [connected, setConnected] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [alpha, setAlpha] = useState('#');
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [winnerSummary, setWinnerSummary] = useState({ winners: [] });
  const [watchmode, setWatchmode] = useState({ sources: [] });
  const [omdbFull, setOmdbFull] = useState({ _for: null, data: null });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const requiresHostPlan = !!user && user.role !== 'owner' && user.planStatus !== 'active';

  const getImdbFromSelected = (sel) => {
    if (!sel) return null;
    if (sel.omdb?.imdbID) return sel.omdb.imdbID;
    const guid = sel.guid || '';
    const match = guid.match(/imdb.*?(tt\d{7,8})/i);
    return match ? match[1] : null;
  };

  // Compute filtered and paginated items (no hooks to avoid conditional hook issues)
  const filtered = movies.filter(m => {
    const t = (m.title || '').trim();
    if (!t) return false;
    const first = t[0].toUpperCase();
    return alpha === '#' ? (first < 'A' || first > 'Z') : first === alpha;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  useEffect(() => {
    if (!user || (user.role !== 'owner' && user.role !== 'admin')) return;
    
    setLoading(true);
    setError('');
    
    // Load Plex status
    plexStatus()
      .then(s => { 
        setConnected(!!s.connected); 
        setHasToken(!!s.hasToken);
        if (s.error) {
          setError(`Plex Status Error: ${s.error}`);
        }
      })
      .catch(err => {
        setConnected(false);
        setError(`Failed to get Plex status: ${err.message || 'Unknown error'}`);
      });
      
    // Load Plex movies
    plexMovies()
      .then(r => {
        const list = r.movies || [];
        setMovies(list);
        
        // If movies are present, consider the library effectively connected
        if (list.length > 0) {
          setConnected(true);
        } else if (r.error) {
          setError(`Plex Movies Error: ${r.error}`);
        }
      })
      .catch(err => {
        setMovies([]);
        setError(`Failed to load Plex movies: ${err.message || 'Unknown error'}`);
      })
      .finally(() => setLoading(false));
      
    getWinnersSummary().then(setWinnerSummary).catch(() => setWinnerSummary({ winners: [] }));
  }, [user]);

  // Fetch OMDb and Watchmode for the selected item
  useEffect(() => {
    if (!selected) return;
    const imdbID = getImdbFromSelected(selected);
    // Reset per-selection state to avoid stale display
    setOmdbFull(prev => (prev._for === imdbID ? prev : { _for: null, data: null }));
    setWatchmode(prev => (prev._for === (imdbID || selected.title) ? prev : { sources: [], vod: [], _for: null }));

    if (imdbID && omdbFull._for !== imdbID) {
      getMovieByImdb(imdbID)
        .then((d) => setOmdbFull({ _for: imdbID, data: d || null }))
        .catch(() => setOmdbFull({ _for: imdbID, data: null }));
    } else if (!imdbID && selected.title) {
      const y = selected.year || '';
      getMovieByTitle(selected.title, y)
        .then((d) => setOmdbFull({ _for: selected.title, data: d || null }))
        .catch(() => setOmdbFull({ _for: selected.title, data: null }));
    }
    if (!requiresHostPlan) {
      const key = imdbID || selected.title;
      const doFetch = () => {
        if (imdbID) {
          return getWatchmodeSources(imdbID);
        } else {
          return api.get('/movies/watchmode/', { params: { title: selected.title, year: selected.year || '', region: 'US' } }).then(r => r.data);
        }
      };
      doFetch()
        .then((d) => setWatchmode({ ...(d || { sources: [], vod: [] }), _for: key }))
        .catch(() => setWatchmode({ sources: [], vod: [], _for: key }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const connectPlex = async () => {
    if (!user || user.role !== 'owner') return;
    try {
      const pin = await api.post('/plex/oauth/pin').then(r => r.data);
      const win = window.open(pin.authUrl, 'plexAuth', 'width=600,height=800');
      // Poll PIN status
      const timer = setInterval(async () => {
        try {
          const status = await api.get(`/plex/oauth/pin/${pin.id}`).then(r => r.data);
          if (status.authorized) {
            clearInterval(timer);
            if (win) win.close();
            setConnected(true);
            setHasToken(true);
            // Refresh library
            plexMovies().then(r => setMovies(r.movies || [])).catch(() => setMovies([]));
          }
        } catch (_) {}
      }, 1500);
      // Safety: stop after 2 minutes
      setTimeout(() => clearInterval(timer), 120000);
    } catch (e) {}
  };

  const retryDiscovery = async () => {
    if (!user || (user.role !== 'owner' && user.role !== 'admin')) return;
    
    setLoading(true);
    setError('');
    
    try {
      const s = await plexStatus();
      setHasToken(!!s.hasToken);
      setConnected(!!s.connected);
      
      if (s.error) {
        setError(`Retry Status Error: ${s.error}`);
      }
      
      if (!s.connected) {
        const r = await plexMovies();
        const list = r.movies || [];
        setMovies(list);
        
        if (list.length > 0) {
          setConnected(true);
        } else if (r.error) {
          setError(`Retry Movies Error: ${r.error}`);
        }
      }
    } catch (err) {
      setError(`Retry failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <Typography>Sign in to access Plex</Typography>;
  if (user.role !== 'owner' && user.role !== 'admin') return (
    <Box>
      <Typography variant="h4" gutterBottom>Plex Library</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip color="warning" size="small" icon={<LockIcon fontSize="small" />} label="Admin or Owner required" />
      </Stack>
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Plex Library {connected ? '' : '(Not Connected)'}</Typography>
        {!connected && (user?.role === 'owner') && (
          hasToken ? (
            <Button variant="outlined" onClick={retryDiscovery} disabled={loading}>
              {loading ? 'Retrying...' : 'Retry Plex'}
            </Button>
          ) : (
            <Button variant="contained" onClick={connectPlex}>Connect Plex</Button>
          )
        )}
      </Stack>
      
      {/* Error Display */}
      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="h6" gutterBottom>Connection Error</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => setError('')} 
            sx={{ mt: 1, color: 'inherit', borderColor: 'currentColor' }}
          >
            Dismiss
          </Button>
        </Paper>
      )}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {['#',...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'].map((ch) => (
          <Chip key={ch} label={ch} size="small" color={alpha===ch?'primary':'default'} onClick={() => { setAlpha(ch); setPage(1); }} />
        ))}
      </Box>
      <Paper>
        <List>
          {pageItems.filter(m => m && m.key).map((m) => (
            <ListItem key={`${m.librarySectionID}-${m.key}`} divider button onClick={(e) => {
              // ignore clicks on title anchor; only gray row clicks open drawer
              if (e.target?.closest('a')) return;
              setSelected(m);
              setDrawerOpen(true);
            }}>
              <ListItemAvatar>
                <Avatar variant="rounded" src={m.thumb ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(m.thumb)}`) : undefined} alt={`${m.title} poster`} />
              </ListItemAvatar>
              <ListItemText
                primary={<a href={m.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{m.title} {m.year ? `(${m.year})` : ''}</a>}
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    {m.librarySectionTitle && <Chip label={m.librarySectionTitle} size="small" />}
                    {m.duration && <Chip label={`${Math.round((+m.duration)/60000)} min`} size="small" />}
                    {m.omdb?.genres && <Chip label={m.omdb.genres} size="small" />}
                    {(m.omdb?.imdbRating || m.omdb?.imdbVotes) && (
                      <Chip label={`IMDb ${m.omdb?.imdbRating || '-'}${m.omdb?.imdbVotes ? ` (${m.omdb.imdbVotes})` : ''}`} size="small" />
                    )}
                    <Chip label="On Plex" color="success" size="small" />
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination count={totalPages} page={safePage} onChange={(_, p) => setPage(p)} />
      </Box>
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 420 },
            p: 2,
            top: { xs: '56px', sm: '64px' },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' }
          }
        }}>
        {selected ? (
          <Box>
            <Box sx={{ position: 'sticky', top: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton aria-label="close" onClick={() => setDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="h6" gutterBottom>{selected.title} {selected.year ? `(${selected.year})` : ''}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src={selected.art ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(selected.art)}`) : (selected.thumb ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(selected.thumb)}`) : undefined)}
                alt={selected.title}
                style={{ maxWidth: '100%', borderRadius: 8 }}
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
              {selected.librarySectionTitle && <Chip label={selected.librarySectionTitle} size="small" />}
              {selected.duration && <Chip label={`${Math.round((+selected.duration)/60000)} min`} size="small" />}
              {selected.omdb?.genres && <Chip label={selected.omdb.genres} size="small" />}
              {(selected.omdb?.imdbRating || selected.omdb?.imdbVotes) && (
                <Chip label={`IMDb ${selected.omdb?.imdbRating || '-'}${selected.omdb?.imdbVotes ? ` (${selected.omdb.imdbVotes})` : ''}`} size="small" />
              )}
              <Chip label="On Plex" color="success" size="small" />
            </Stack>

            {/* OMDb details: Plot, Director, Cast */}
            {(() => {
              const guid = selected.guid || '';
              const match = guid.match(/imdb.*?(tt\d{7,8})/i);
              const imdbID = selected.omdb?.imdbID || (match ? match[1] : null);
              if (imdbID && omdbFull._for !== imdbID) {
                getMovieByImdb(imdbID).then((d) => setOmdbFull({ _for: imdbID, data: d || null })).catch(() => setOmdbFull({ _for: imdbID, data: null }));
              }
              const key = imdbID || selected.title;
              const d = omdbFull._for === key ? omdbFull.data : null;
              if (!d) return null;
              const toPeopleLinks = (csv) => (csv || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map((name, idx) => (
                  <Button key={`p-${idx}`} size="small" variant="text" sx={{ px: 0.5, minWidth: 0 }}
                    href={`https://www.imdb.com/find/?q=${encodeURIComponent(name)}&s=nm`} target="_blank" rel="noreferrer">
                    {name}
                  </Button>
                ));
              return (
                <Box sx={{ mb: 1 }}>
                  {d.Plot && d.Plot !== 'N/A' && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {d.Plot}
                    </Typography>
                  )}
                  {(d.Director && d.Director !== 'N/A') && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Director: </strong>{toPeopleLinks(d.Director)}
                    </Typography>
                  )}
                  {(d.Actors && d.Actors !== 'N/A') && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Cast: </strong>{toPeopleLinks(d.Actors)}
                    </Typography>
                  )}
                </Box>
              );
            })()}
            <Divider sx={{ my: 1 }} />
            {/* History (wins, timestamps, votes) */}
            {(() => {
              const guid = selected.guid || '';
              const match = guid.match(/imdb.*?(tt\d{7,8})/i);
              const imdbID = selected.omdb?.imdbID || (match ? match[1] : null);
              const norm = (s) => (s || '').toString().trim().toLowerCase();
              const sameTitleYear = (w) => norm(w.title) === norm(selected.title) && (!!selected.year ? String(w.year) === String(selected.year) : true);
              const wins = (winnerSummary.winners || []).filter(w => (imdbID && w.imdbID && norm(w.imdbID) === norm(imdbID)) || sameTitleYear(w));
              if (wins.length === 0) return (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No past wins found on ReelConsensus.</Typography>
              );
              const timesWon = wins[0].timesWon || wins.length;
              return (
                <Box sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                    <Chip label={`Won ${timesWon} ${timesWon===1?'time':'times'}`} size="small" color="primary" />
                    {wins[0].ratingCount ? <Chip label={`Avg Rating ${wins[0].ratingAvg} (${wins[0].ratingCount})`} size="small" /> : null}
                  </Stack>
                  <Typography variant="subtitle2" gutterBottom>Win timeline</Typography>
                  <List dense>
                    {wins.filter(w => w && w.pollId).map((w) => (
                      <ListItem key={`${w.pollId}-${w.wonAt}`} sx={{ px: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ListItemText
                          primary={w.pollTitle || 'Poll'}
                          secondary={`${new Date(w.wonAt).toLocaleString()} • ${w.votes} votes`}
                        />
                        <Button size="small" variant="text" onClick={() => navigate(`/results/${w.pollId}`)}>View Results</Button>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })()}

            <Divider sx={{ my: 1 }} />
            {/* Where to Watch (Watchmode) */}
            {(() => {
              const guid = selected.guid || '';
              const match = guid.match(/imdb.*?(tt\d{7,8})/i);
              const imdbID = match ? match[1] : null;
              if (requiresHostPlan) {
                return (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>Where to Watch</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Watchmode streaming availability is a Host plan feature.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="contained" onClick={() => (window.location.href = '/pricing')}>Subscribe</Button>
                    </Stack>
                  </Box>
                );
              }
              if (!watchmode.sources || watchmode._for !== (imdbID || selected.title)) {
                const params = imdbID ? undefined : { params: { title: selected.title, year: selected.year || '', region: 'US' } };
                const req = imdbID ? getWatchmodeSources(imdbID) : api.get('/movies/watchmode/', params).then(r => r.data);
                req.then((d) => setWatchmode({ ...d, _for: imdbID || selected.title })).catch(() => setWatchmode({ sources: [], _for: imdbID || selected.title }));
              }
              const key = imdbID || selected.title;
              const sources = (watchmode._for === key ? (watchmode.sources || []) : []);
              if (!imdbID && sources.length === 0) {
                return (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">No streaming sources detected.</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      <a href="https://watchmode.com" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                        Streaming data powered by Watchmode.com
                      </a>
                    </Typography>
                  </Box>
                );
              }
              const vod = (watchmode._for === key ? (watchmode.vod || []) : []);
              return (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>Watch now</Typography>
                  {sources.length > 0 ? (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
                      {sources.filter(s => s && s.source_id).map((s, idx) => (
                        <Button key={`${s.source_id}-${idx}`} size="small" variant="outlined" onClick={() => window.open(s.web_url, '_blank')}>
                          {s.name}
                        </Button>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No subscription/free streaming sources.</Typography>
                  )}
                  {vod.length > 0 && (
                    <>
                      <Typography variant="subtitle2">Rent/Buy</Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {vod.filter(s => s && s.source_id).map((s, idx) => (
                          <Button key={`vod-${s.source_id}-${idx}`} size="small" variant="text" onClick={() => window.open(s.web_url, '_blank')}>
                            {s.name}
                          </Button>
                        ))}
                      </Stack>
                    </>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    <a href="https://watchmode.com" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                      Streaming data powered by Watchmode.com
                    </a>
                  </Typography>
                </Box>
              );
            })()}

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" gutterBottom>Actions</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => window.open(selected.url, '_blank')}>Open in Plex</Button>
              <Button variant="outlined" onClick={() => {
                // Quick-create poll with this one preselected in step 2
                const pre = [{ Title: selected.title, Year: selected.year, Poster: selected.thumb ? buildApiUrl(`/api/plex/image?path=${encodeURIComponent(selected.thumb)}`) : null, Type: 'movie', imdbID: selected.omdb?.imdbID }];
                localStorage.setItem('prefillPollMovies', JSON.stringify(pre));
                window.location.href = '/create';
              }}>Create Poll</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Tip: On the Create Poll page, your selection will be prefilled.
            </Typography>
          </Box>
        ) : (
          <Typography>Select a movie to see details</Typography>
        )}
      </Drawer>
    </Box>
  );
};

export default PlexLibrary;
